// hooks/useUserPreferences.js
// TEMPORARY FIX - endpoints defined inline to bypass import issues

import { useState, useEffect, useCallback, useRef } from 'react';
import apiService from '../services/api';

// Temporary inline endpoints until we fix the import issue
const USER_PREF_ENDPOINTS = {
  USER_PREFERENCE: (pageId, preferenceKey) => `/user-preferences/${pageId}/${preferenceKey}`,
  USER_PREFERENCES_PAGE: (pageId) => `/user-preferences/${pageId}`,
  USER_PREFERENCES_EXPORT: '/user-preferences/export/all',
};

/**
 * Custom hook for managing a single user preference
 * 
 * @param {string} pageId - Page identifier (e.g., 'customer_breakdown')
 * @param {string} preferenceKey - Preference key (e.g., 'hidden_clients')
 * @param {any} defaultValue - Default value if preference doesn't exist
 * @returns {[value, setValue, loading, error, initialized]}
 */
export const useUserPreference = (pageId, preferenceKey, defaultValue = null) => {
  const [value, setValue] = useState(defaultValue);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);

  // Load preference from backend on mount
  useEffect(() => {
    const loadPreference = async () => {
      const startTime = performance.now();
      
      try {
        setLoading(true);
        setError(null);

        const endpoint = USER_PREF_ENDPOINTS.USER_PREFERENCE(pageId, preferenceKey);
        const response = await apiService.get(endpoint);

        setValue(response.preference_value);

        const totalTime = (performance.now() - startTime).toFixed(0);
        // console.log(`Preference ${pageId}/${preferenceKey} loaded in ${totalTime}ms`);

      } catch (err) {
        // 404 is expected when preference doesn't exist yet
        if (err.response?.status === 404 || err.message?.includes('404')) {
        //   console.log(`Preference ${pageId}/${preferenceKey} not found, using default`);
          setValue(defaultValue);
        } else {
          console.error('Error loading preference:', err);
          setError(err.message || 'Failed to load preference');
          setValue(defaultValue);
        }
      } finally {
        setLoading(false);
        setInitialized(true);
      }
    };

    loadPreference();
  }, [pageId, preferenceKey]);

  // Save preference to backend
  const saveValue = useCallback(async (newValue) => {
    try {
      const endpoint = USER_PREF_ENDPOINTS.USER_PREFERENCE(pageId, preferenceKey);
      await apiService.post(endpoint, newValue);

      setValue(newValue);
      setError(null);
    } catch (err) {
      console.error('Error saving preference:', err);
      setError(err.message || 'Failed to save preference');
    }
  }, [pageId, preferenceKey]);

  return [value, saveValue, loading, error, initialized];
};

/**
 * Hook for managing multiple preferences on a page
 * 
 * @param {string} pageId - Page identifier
 * @param {Object} defaultPreferences - Object with default values for all preferences
 * @returns {[preferences, setPreference, setMultiplePreferences, loading, error, initialized]}
 */
export const usePagePreferences = (pageId, defaultPreferences = {}) => {
  const [preferences, setPreferences] = useState(defaultPreferences);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);
  
  // Use ref to store defaults to avoid dependency issues
  const defaultsRef = useRef(defaultPreferences);

  // Load all preferences for page on mount
  useEffect(() => {
    const loadPreferences = async () => {
      const startTime = performance.now();
      
      try {
        setLoading(true);
        setError(null);

        const endpoint = USER_PREF_ENDPOINTS.USER_PREFERENCES_PAGE(pageId);
        // console.log('Loading preferences from:', endpoint); // DEBUG
        
        const response = await apiService.get(endpoint);

        // Convert array of preferences to object
        const prefObject = {};
        if (response.preferences && Array.isArray(response.preferences)) {
          response.preferences.forEach(pref => {
            prefObject[pref.preference_key] = pref.preference_value;
          });
        }

        // Merge with defaults
        setPreferences({ ...defaultsRef.current, ...prefObject });

        const totalTime = (performance.now() - startTime).toFixed(0);
        // console.log(`Page preferences for ${pageId} loaded in ${totalTime}ms`);

      } catch (err) {
        console.error('Error loading page preferences:', err);
        setError(err.message || 'Failed to load page preferences');
        setPreferences(defaultsRef.current);
      } finally {
        setLoading(false);
        setInitialized(true);
      }
    };

    loadPreferences();
  }, [pageId]);

  // Set a single preference
  const setPreference = useCallback(async (key, value) => {
    try {
      const endpoint = USER_PREF_ENDPOINTS.USER_PREFERENCE(pageId, key);
      await apiService.post(endpoint, value);

      setPreferences(prev => ({
        ...prev,
        [key]: value
      }));
      setError(null);
    } catch (err) {
      console.error('Error saving preference:', err);
      setError(err.message || 'Failed to save preference');
    }
  }, [pageId]);

  // Set multiple preferences at once
  const setMultiplePreferences = useCallback(async (prefsToSet) => {
    try {
      const endpoint = USER_PREF_ENDPOINTS.USER_PREFERENCES_PAGE(pageId);
      await apiService.post(endpoint, prefsToSet);

      setPreferences(prev => ({
        ...prev,
        ...Object.keys(prefsToSet).reduce((acc, key) => {
          acc[key] = prefsToSet[key];
          return acc;
        }, {})
      }));
      setError(null);
    } catch (err) {
      console.error('Error saving multiple preferences:', err);
      setError(err.message || 'Failed to save preferences');
    }
  }, [pageId]);

  return [preferences, setPreference, setMultiplePreferences, loading, error, initialized];
};

/**
 * Utility function to export all user preferences
 */
export const exportAllPreferences = async () => {
  try {
    const endpoint = USER_PREF_ENDPOINTS.USER_PREFERENCES_EXPORT;
    const response = await apiService.get(endpoint);
    return response;
  } catch (err) {
    console.error('Error exporting preferences:', err);
    throw err;
  }
};

/**
 * Utility function to delete a preference
 */
export const deletePreference = async (pageId, preferenceKey) => {
  try {
    const endpoint = USER_PREF_ENDPOINTS.USER_PREFERENCE(pageId, preferenceKey);
    const response = await apiService.delete(endpoint);
    return response;
  } catch (err) {
    console.error('Error deleting preference:', err);
    throw err;
  }
};